import { yupResolver } from '@hookform/resolvers/yup'
import { useDisabledPromoCodesList } from '@if/core'
import { compareAsc, DateOnly } from '@if/core/date'
import {
  EFinancingProgram,
  EPaymentFrequency,
  EPaymentPlan,
  EPlanType,
  EProvince,
  EWorksheetStatus,
} from '@if/core/types'
import { formatCurrency, formatDate, normalizeNumber } from '@if/core/utils'
import { ControlledStringDateOnlyPicker } from '@if/mui'
import { ErrorOutline } from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { Breadcrumb, InputTextField, PageSpinner, SelectComponent } from '@src/components'
import MerchantPaymentInstructions from '@src/containers/ViewCreditApplicationPage/components/MerchantPaymentInstructions'
import PrivateMessage from '@src/containers/ViewCreditApplicationPage/components/privateMessage'
import {
  canComputeFunding,
  getEmptyComputedFundingDto,
  getHighCostRateForProvince,
} from '@src/containers/ViewWorksheet/viewWorksheet-selectors'
import { useCreditApplicationContext, useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useGetAvailableCredit } from '@src/data/api/available-credit-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import {
  useComputeIfXpressFunding,
  useUpdateIfXpressWorksheet,
} from '@src/data/api/ifxpress-worksheet/ifxpress-worksheet-api'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import { useDeleteMessage } from '@src/data/api/message/message-api'
import {
  useFinancingConfig,
  useProvinceConfigurations,
  useSetBusinessErrors,
} from '@src/data/selectors/app-config-selectors'
import { CreditApplicationMessage } from '@src/data/types'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import {
  buildEditIfXpressWorksheetDtoSchema,
  EditIfXpressWorksheetDto,
  IfXpressWorksheet,
} from '@src/data/types/IfXpressWorksheetSchema'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { formatPaymentPlanForSummary } from '@src/services/Formatter'
import { debounce } from 'lodash-es'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import AvailableCreditTooltip from '../../../components/AvailableCreditTooltip'
import {
  useAprTooHigh,
  useEditTotalObligation,
  useFirstPaymentDateOptions,
  usePaymentPlanList,
  usePossibleTerms,
} from '../editWorksheet-hooks'

const EditIfXpressWorksheetPage = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()

  // Get credit application from context instead of Redux
  const { creditApplication } = useCreditApplicationData()
  const financingConfig = useFinancingConfig(creditApplication?.financingProgramId)

  const worksheetYupResolver = useMemo(
    () => yupResolver(buildEditIfXpressWorksheetDtoSchema(financingConfig)),
    [financingConfig],
  )

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { errors },
  } = useForm<EditIfXpressWorksheetDto>({
    mode: 'onBlur',
    defaultValues: {} as EditIfXpressWorksheetDto,
    resolver: worksheetYupResolver,
    context: { loanPurposeId: creditApplication?.loanPurposeId },
  })

  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()
  const [deleteMessage] = useDeleteMessage()
  const theme = useTheme()
  const [updateWorksheet, isUpdatingWorksheet] = useUpdateIfXpressWorksheet()
  const [computeFunding, isComputingFunding] = useComputeIfXpressFunding()

  const { id: creditAppId, financingProgramId } = useCreditApplicationContext()
  const [availableCreditData] = useGetAvailableCredit(financingProgramId, creditAppId)
  const availableCredit = availableCreditData?.availableAmount ?? 0
  const creditInProgress = availableCreditData?.creditInProgress ?? 0
  const { isLoading: isLoadingCreditApplication } = useCreditApplicationData()
  const isLoading = isUpdatingWorksheet
  const setBusinessErrors = useSetBusinessErrors()
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')

  // Get worksheet and private messages from context instead of Redux
  const { worksheet, privateMessages } = useCreditApplicationData()
  const ifXpressWorksheet = worksheet as IfXpressWorksheet | undefined

  // Local state for computed info (no longer in Redux)
  const [computedInfo, setComputedInfo] = useState<FundingComputedDto>({} as FundingComputedDto)
  // True from the instant a relevant field changes until the (debounced) funding
  // recompute settles. Lets the save button disable immediately instead of waiting
  // for the debounce + API request to actually start.
  const [isComputeFundingPending, setIsComputeFundingPending] = useState(false)

  // Compute total fees and insurance inline (no Redux selectors)
  const totalFees = computedInfo?.lenderFee || 0
  const totalInsurance = (computedInfo?.insuranceFee || 0) + (computedInfo?.insuranceTax || 0)
  const disabledPromoList = useDisabledPromoCodesList(
    merchant?.planPromoRules,
    creditApplication?.normsCreditDecision,
    creditApplication?.financingProgramId ?? EFinancingProgram.Personal,
  )
  const paymentPlanList = usePaymentPlanList(
    creditApplication?.createdOn,
    merchant?.paymentPlans,
    disabledPromoList,
    ifXpressWorksheet?.paymentPlanId,
  )

  const provinceConfigs = useProvinceConfigurations()
  const aprTooHigh = useAprTooHigh(computedInfo, creditApplication?.applicant.currentAddress.stateIso, provinceConfigs)
  const user = useUser()
  const firstPaymentOn = watch('firstPaymentOn')
  const amountRequested = normalizeNumber(watch('amountRequested')?.toString()) ?? 0
  const [isVariableInterest, setIsVariableInterest] = useState(false)

  const possibleTerms = usePossibleTerms(creditApplication?.finalCreditDecision.maxTermDuration, isVariableInterest)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const easternTime = new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' })
  const currentDateAsEasternTimeZone = new Date(easternTime)

  useEffect(() => {
    if (amountRequested) trigger('term').catch(reportErrorToConsole)
  }, [amountRequested, trigger])

  // dynamic values
  const paymentFrequency = watch('paymentFrequency') as EPaymentFrequency
  const paymentPlanId = watch('paymentPlanId') as EPaymentPlan
  const deliveryOn = watch('deliveryOn')
  const term = watch('term')
  const includeInsurance = watch('includeInsurance')

  const isNFl =
    creditApplication?.applicant.currentAddress.stateIso &&
    creditApplication.applicant.currentAddress.stateIso === EProvince.Newfoundland

  const highCostRateForProvince = getHighCostRateForProvince(
    provinceConfigs,
    creditApplication?.applicant.currentAddress.stateIso,
  )
  const totalObligation = useEditTotalObligation(amountRequested, computedInfo)
  const planName = creditApplication?.normsCreditDecision?.planName ?? EPlanType.Default

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.IfXpress}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  const selectedPlan = useMemo(
    () => merchant?.paymentPlans?.find((p) => p.id === paymentPlanId),
    [merchant?.paymentPlans, paymentPlanId],
  )

  // reset form on worksheet changed
  useEffect(() => {
    if (ifXpressWorksheet && merchant) {
      reset(ifXpressWorksheet as EditIfXpressWorksheetDto)
      setValue('firstPaymentOn', ifXpressWorksheet.firstPaymentOn ?? formatDate(new Date()))
    }
  }, [ifXpressWorksheet, merchant, reset, setValue])

  const doComputeFunding = useCallback(
    async (computeDto: FundingInfoDto) => {
      try {
        const data = await computeFunding(computeDto)
        setComputedInfo(data)
      } catch (error) {
        reportErrorToConsole(error)
      } finally {
        setIsComputeFundingPending(false)
      }
    },
    [computeFunding],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceComputeFunding = useCallback(debounce(doComputeFunding, 500), [doComputeFunding])

  // recompute funding
  useEffect(() => {
    const interestRate = creditApplication?.finalCreditDecision.interestRate ?? 0
    const hasCoApplicant = creditApplication?.coApplicant !== null
    const stateIso = creditApplication?.applicant.currentAddress.stateIso
    const requestId = nanoid()
    if (
      amountRequested >= financingConfig.minimumLoanAmount &&
      paymentFrequency &&
      term &&
      deliveryOn &&
      paymentPlanId &&
      firstPaymentOn &&
      stateIso &&
      ifXpressWorksheet &&
      merchant
    ) {
      const computeDto: FundingInfoDto = {
        financingProgramId: creditApplication?.financingProgramId,
        amountRequested,
        includeInsurance,
        insuranceCompanyId: ifXpressWorksheet.insuranceCompanyId,
        paymentFrequency,
        paymentPlanId,
        term,
        interestRate,
        hasCoApplicant,
        stateIso,
        deliveryOn: new DateOnly(deliveryOn),
        firstPaymentOn,
        requestId,
        creditApplicationId: creditApplication.id,
        merchantId: merchant.id,
      }

      if (canComputeFunding(computeDto, financingConfig.minimumLoanAmount)) {
        setIsComputeFundingPending(true)
        void debounceComputeFunding(computeDto)
      } else {
        const emptyComputedDto = getEmptyComputedFundingDto()

        setComputedInfo(emptyComputedDto)
        setIsComputeFundingPending(false)
      }
    }
  }, [
    amountRequested,
    paymentFrequency,
    paymentPlanId,
    deliveryOn,
    firstPaymentOn,
    term,
    includeInsurance,
    creditApplication,
    id,
    debounceComputeFunding,
    merchant?.id,
    merchant,
    ifXpressWorksheet,
    financingConfig.minimumLoanAmount,
  ])

  useEffect(() => {
    const isVariable = Boolean(paymentPlanId && paymentPlanId !== EPaymentPlan.RegularDailyInterests)

    if (isVariable && selectedPlan) {
      setValue('term', selectedPlan.loanTerm)
    }

    setIsVariableInterest(isVariable)
  }, [paymentPlanId, selectedPlan, selectedPlan?.loanTerm, setValue])

  const getMerchantFees = () => {
    if (selectedPlan) return formatCurrency((selectedPlan.merchantFeeRate / 100) * amountRequested)
    return ''
  }

  const handleSave = async () => {
    const formData = getValues()
    await onFormSubmitted(formData)
  }

  const onFormSubmitted = async (data: EditIfXpressWorksheetDto) => {
    setIsSubmitting(true)
    if (isDialogOpen) {
      setIsDialogOpen(false)
    }
    const expiry = new Date(creditApplication!.expiresOn)
    const hardHitReport = creditApplication!.applicant.hardHitReport || creditApplication!.coApplicant?.hardHitReport
    const receivedOnPlus90Days = new Date(hardHitReport.receivedOn)
    receivedOnPlus90Days.setDate(receivedOnPlus90Days.getDate() + 90)
    const deliveryOnDate = new Date(`${getValues('deliveryOn')}T20:00:00`) // 20PM Local Time
    const deliveryOnUTC = new Date(`${getValues('deliveryOn')}T00:00:00`)
    const deliveryOnDateAsEasternTimeZone = new Date(
      deliveryOnDate.toLocaleString('en-US', { timeZone: 'America/Toronto' }),
    )
    const baseActivationDateAsEasternTimeZone = new Date(
      currentDateAsEasternTimeZone.getFullYear(),
      currentDateAsEasternTimeZone.getMonth(),
      currentDateAsEasternTimeZone.getDate(),
      0,
      0,
      0,
    )
    const firstPaymentOnDate = new Date(`${getValues('firstPaymentOn')}T00:00:00`)

    try {
      if (
        isNFl
          ? computedInfo.effectiveRate > highCostRateForProvince
          : computedInfo.effectiveRate > financingConfig.effectiveRateThreshold
      ) {
        setBusinessErrors([
          {
            message: t('worksheetCommon.effectiveRateTooHigh', '', {
              effectiveRate: computedInfo.effectiveRate,
              maxRate: isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold,
            }),
          },
        ])
      } else if (compareAsc(deliveryOnDateAsEasternTimeZone, baseActivationDateAsEasternTimeZone) === -1) {
        setBusinessErrors([{ message: 'worksheetCommon.activationDateInPast' }])
      } else if (amountRequested > availableCredit) {
        setBusinessErrors([{ message: 'worksheetCommon.amountRequestedHigherThanAvailableCredit' }])
      } else if (compareAsc(deliveryOnUTC, receivedOnPlus90Days) === 1)
        setBusinessErrors([{ message: 'worksheetCommon.activationDateReport90Days' }])
      else if (compareAsc(deliveryOnUTC, expiry) === 1)
        setBusinessErrors([{ message: 'worksheetCommon.deliveryOnEarlierExpiration' }])
      else if (minFirstPaymentDate > firstPaymentOnDate) {
        setBusinessErrors([
          {
            message: t('worksheetCommon.firstPaymentDateInPast', {
              minFirstPaymentDate: formatDate(minFirstPaymentDate),
            }),
          },
        ])
      } else if (maxFirstPaymentDate < firstPaymentOnDate) {
        setBusinessErrors([
          {
            message: t('worksheetCommon.firstPaymentDateTooFar', {
              maxFirstPaymentDate: formatDate(maxFirstPaymentDate),
            }),
          },
        ])
      } else {
        data.amountRequested = amountRequested
        data.status = EWorksheetStatus.Active
        try {
          await updateWorksheet(data)
          await invalidateFullCreditApplication(creditApplication!.id, creditApplication!.financingProgramId)
          navigate(`/Applications/${EFinancingProgram.IfXpress}/${creditApplication!.id}/view`, {
            replace: true,
          })
        } catch (error) {
          reportErrorToConsole(error)
        } finally {
          setIsSubmitting(false)
        }
        return
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const { minFirstPaymentDate, maxFirstPaymentDate } = useFirstPaymentDateOptions({
    financingProgramId: creditApplication?.financingProgramId,
    creditApplicationId: creditApplication?.id,
    deliveryOn: new DateOnly(deliveryOn),
    paymentFrequency,
    isHighEstimate: ifXpressWorksheet?.isHighEstimate ?? false,
    setValue,
    getValues,
  })

  const handleDeletingPrivateMessage = (message: CreditApplicationMessage) => {
    if (creditApplication?.id) {
      return deleteMessage({
        id: message.id,
        creditApplicationId: message.creditApplicationId,
        financingProgramId: creditApplication.financingProgramId,
      })
    }
    return null
  }

  return (
    <div>
      <PageSpinner withBackdrop isLoading={(isLoading && isLoadingCreditApplication) || isSubmitting} />

      <Breadcrumb trees={breadCrumbs} />
      <form onSubmit={handleSubmit(onFormSubmitted, reportErrorToConsole)}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <InputTextField
            value={t(`EPlanType.${planName}`, { ns: 'enum' })}
            label={t('worksheetCommon.program')}
            disabled
          />
          <InputTextField disabled value={creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0} />
          <AvailableCreditTooltip availableCreditData={availableCreditData}>
            <div style={{ position: 'relative' }}>
              <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />
              {creditInProgress !== 0 && (
                <ErrorOutline
                  sx={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: theme.palette.error.main,
                    fontSize: '1.5rem',
                  }}
                />
              )}
            </div>
          </AvailableCreditTooltip>
          <InputTextField label={t('worksheetCommon.availableCredit')} disabled value={availableCredit} />

          <div style={{ marginTop: '1%' }}>
            <Button
              variant="contained"
              disabled={aprTooHigh || isComputeFundingPending || isComputingFunding || !computedInfo || isLoading}
              sx={{ width: 'auto', whiteSpace: 'nowrap', textAlign: 'left' }}
              onClick={handleSave}
            >
              {t('common.save')}
            </Button>
          </div>
        </Stack>
        {creditApplication && (
          <PrivateMessage
            messages={privateMessages}
            handleDeletingPrivateMessage={handleDeletingPrivateMessage}
            currentUser={user}
            creditAppCreationDate={creditApplication.createdOn}
          />
        )}
        {merchant && <MerchantPaymentInstructions merchant={merchant} />}
        <Grid container marginTop={5}>
          <Grid size={8}>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <InputTextField
                  slotProps={{
                    input: {
                      endAdornment: '$',
                    },
                  }}
                  {...register('amountRequested')}
                  error={errors?.amountRequested}
                  label={t('worksheetCommon.amountRequested')}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <ControlledStringDateOnlyPicker
                  name="deliveryOn"
                  control={control}
                  label={t('worksheetCommon.activationDate')}
                  disablePast
                  minDate={currentDateAsEasternTimeZone}
                  disabled={creditApplication?.editLocked}
                  fullWidth
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <ControlledStringDateOnlyPicker
                  name="firstPaymentOn"
                  control={control}
                  label={t('worksheetCommon.firstPaymentDate')}
                  disablePast
                  minDate={minFirstPaymentDate}
                  maxDate={maxFirstPaymentDate}
                  fullWidth
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={paymentPlanList}
                  label={t('worksheet.paymentPlan')}
                  {...register('paymentPlanId')}
                  error={errors?.paymentPlanId}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={[{ label: t('EPaymentFrequency.monthly', { ns: 'enum' }), value: EPaymentFrequency.Monthly }]}
                  label={t('worksheet.paymentFrequency')}
                  {...register('paymentFrequency')}
                  error={errors?.paymentFrequency}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  disabled={isVariableInterest}
                  items={possibleTerms.map((item) => ({
                    label: `${item} ${t('worksheet.month')}`,
                    value: item,
                  }))}
                  label={t('worksheet.term')}
                  {...register('term')}
                  error={errors?.term}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <FormControlLabel
                  {...register('includeInsurance')}
                  control={<Checkbox defaultChecked={ifXpressWorksheet?.includeInsurance} />}
                  label={t('worksheetCommon.addProtectionPlan')}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={4}>
            <Box
              sx={{
                border: 1,
                padding: 2,
                marginLeft: 5,
                paddingBottom: 10,
              }}
            >
              {isComputingFunding ? (
                <Stack justifyContent="center" alignItems="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </Stack>
              ) : (
                <Stack direction="column" spacing={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('worksheetCommon.fundingSummary')} :
                  </Typography>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.loanAmount')} :</Typography>
                    <Typography>{`${formatCurrency(getValues('amountRequested'))}`}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>
                      {`${t('worksheetCommon.interest')} (${
                        isVariableInterest
                          ? formatPaymentPlanForSummary(selectedPlan)
                          : `${creditApplication?.finalCreditDecision?.interestRate}%`
                      })`}
                      :{' '}
                    </Typography>
                    <Typography>{formatCurrency(computedInfo?.totalInterestAmount)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{`${t('worksheetCommon.financeFee')} (${computedInfo?.lenderFeeRate}%)`}:</Typography>
                    <Typography>{formatCurrency(totalFees)}</Typography>
                  </Stack>

                  {isVariableInterest && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography>
                        {`${t('worksheetCommon.merchantFee')} (${selectedPlan?.merchantFeeRate}%)`} :
                      </Typography>
                      <Typography>{getMerchantFees()}</Typography>
                    </Stack>
                  )}

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.protectionPlan')} :</Typography>
                    <Typography>{formatCurrency(totalInsurance)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.totalObligation')} :</Typography>
                    <Typography>{formatCurrency(totalObligation)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.annualPercentageRate')} :</Typography>
                    <Typography>{computedInfo.effectiveRate} %</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" paddingTop={3}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {t('worksheetCommon.payment')}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.maxPmtAmount')} :</Typography>
                    <Typography>{formatCurrency(creditApplication?.finalCreditDecision.maxPmtAmount)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.paymentFrequency')} :</Typography>
                    <Typography>
                      {ifXpressWorksheet?.paymentFrequency
                        ? t(`EPaymentFrequency.${ifXpressWorksheet?.paymentFrequency}`, { ns: 'enum' })
                        : t('EPaymentFrequency.monthly', { ns: 'enum' })}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.frequencyAmount')} :</Typography>
                    <Typography>{formatCurrency(computedInfo?.paymentForFrequency)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.firstPaymentDate')} :</Typography>
                    <Typography>{formatDate(firstPaymentOn)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" paddingTop={3}>
                    <Typography fontWeight="bold">{t('worksheetCommon.amountToBePaid')} :</Typography>
                    <Typography>{formatCurrency(getValues('amountRequested') ?? 0)}</Typography>
                  </Stack>
                </Stack>
              )}
            </Box>
          </Grid>
        </Grid>
      </form>
    </div>
  )
}
export default EditIfXpressWorksheetPage
